import React, { useEffect, useMemo, useRef } from "react";
import { Spinner, Stack } from "react-bootstrap";

import { GetWorkRequestsByPriorityResponse } from "src/services/Firebase/data";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
  preloadedData?: GetWorkRequestsByPriorityResponse;
}

interface PriorityData {
  priorityId: number;
  priorityName: string;
  priorityColor: string;
  count: number;
}

const WorkOrderPriorityChart = ({ caption, preloadedData }: WidgetProps) => {
  const data = preloadedData;
  const svgRef = useRef<SVGSVGElement>(null);

  const priorityData: PriorityData[] = useMemo(() => {
    if (!data?.widgetsData?.workRequestsPriorityStatistics) return [];
    return data.widgetsData.workRequestsPriorityStatistics;
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || priorityData.length === 0) return;

    const margin = { top: 16, right: 16, bottom: 28, left: 36 };
    const totalWidth = 460;
    const totalHeight = 180;
    const width = totalWidth - margin.left - margin.right;
    const height = totalHeight - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", totalWidth)
      .attr("height", totalHeight)
      .attr("viewBox", `0 0 ${totalWidth} ${totalHeight}`)
      .attr("style", "max-width: 100%; height: auto;")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(priorityData.map((d) => d.priorityName))
      .range([0, width])
      .padding(0.3);

    const maxCount = d3.max(priorityData, (d) => d.count) ?? 0;
    const y = d3
      .scaleLinear()
      .domain([0, maxCount + Math.ceil(maxCount * 0.15)])
      .range([height, 0]);

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("font-size", "10px")
      .style("fill", "#888780");
    svg.select(".domain").style("display", "none");

    svg
      .append("g")
      .call(d3.axisLeft(y).ticks(4).tickFormat(d3.format("d")))
      .selectAll("text")
      .style("font-size", "10px")
      .style("fill", "#888780");
    svg.selectAll(".domain").style("display", "none");

    svg
      .append("g")
      .attr("class", "grid")
      .call(
        d3
          .axisLeft(y)
          .ticks(4)
          .tickSize(-width)
          .tickFormat(() => ""),
      )
      .selectAll("line")
      .style("stroke", "rgba(136,135,128,0.15)")
      .style("stroke-dasharray", "3,3");

    svg.select(".grid .domain").remove();

    svg
      .selectAll(".bar")
      .data(priorityData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.priorityName) ?? 0)
      .attr("y", height)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("fill", (d) => d.priorityColor)
      .attr("rx", 4)
      .style("opacity", 0.9)
      .on("mouseover", function (this: SVGRectElement, _event, d) {
        d3.select(this).style("opacity", 1);
        const tooltip = svg
          .append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${(x(d.priorityName) ?? 0) + x.bandwidth() / 2},${y(d.count) - 8})`);
        tooltip
          .append("rect")
          .attr("x", -24)
          .attr("y", -18)
          .attr("width", 48)
          .attr("height", 18)
          .attr("fill", "rgba(0,0,0,0.7)")
          .attr("rx", 3);
        tooltip
          .append("text")
          .attr("text-anchor", "middle")
          .attr("y", -5)
          .attr("fill", "#ffffff")
          .attr("font-size", "11px")
          .text(d.count);
      })
      .on("mouseout", function (this: SVGRectElement) {
        d3.select(this).style("opacity", 0.9);
        svg.selectAll(".tooltip").remove();
      })
      .transition()
      .duration(700)
      .delay((_, i) => i * 80)
      .ease(d3.easeQuadOut)
      .attr("y", (d) => y(d.count))
      .attr("height", (d) => height - y(d.count));

    svg
      .selectAll(".bar-label")
      .data(priorityData)
      .enter()
      .append("text")
      .attr("class", "bar-label")
      .attr("x", (d) => (x(d.priorityName) ?? 0) + x.bandwidth() / 2)
      .attr("y", (d) => y(d.count) - 4)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("font-weight", "500")
      .attr("fill", "#888780")
      .style("opacity", 0)
      .text((d) => d.count)
      .transition()
      .delay((_, i) => i * 80 + 600)
      .duration(200)
      .style("opacity", 1);
  }, [priorityData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <CardTitle>{caption}</CardTitle>
        </HeaderRow>
        {data ? (
          <>
            <ChartContainer>
              <svg ref={svgRef} />
            </ChartContainer>
            <LegendRow>
              {priorityData.map((item) => (
                <LegendItem key={item.priorityId}>
                  <LegendDot style={{ background: item.priorityColor }} />
                  {item.priorityName} {item.count}
                </LegendItem>
              ))}
            </LegendRow>
          </>
        ) : (
          <Spinner animation="border" />
        )}
      </Stack>
    </WidgetContainer>
  );
};

export default WorkOrderPriorityChart;

const WidgetContainer = styled.div`
  display: flex;
  min-width: 0;
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 16px;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  overflow-x: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${colors.grey.dark};
`;

const LegendDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex-shrink: 0;
`;
